import { JSX, memo } from "react";

import ChartWidget from "../widgets/ChartWidget";
import DataTableWidget from "../widgets/DataTableWidget";
import FunnelWidget from "../widgets/FunnelWidget";
import KpiWidget from "../widgets/KpiWidget";
import KpiWithDetailWidget from "../widgets/KpiWithDetailWidget";
import QuadrantScatterWidget from "../widgets/QuadrantScatterWidget";
import KpiQuadrantWidget from "../widgets/KpiQuadrantWidget";
import TieredBarWidget from "../widgets/TieredBarWidget";
import SparkleWidget from "../widgets/SparkleWidget";
import CustomBarWidget from "../widgets/CustomBarWidget";
import CustomTableWidget from "../widgets/CustomTableWidget";
import InfoBarWidget from "../widgets/InfoBarWidget";
import DonutPieWidget from "../widgets/DonutPieWidget";
import SingleBarWidget from "../widgets/SingleBarWidget";
import DenialReasonsWidget from "../widgets/DenialReasonsWidget";
import DeviationBarWidget from "../widgets/DeviationBarWidget";
import AverageBarWidget from "../widgets/AverageBarWidget";
import CardWidget from "../widgets/CardWidget";
import BarDrilldownWidget from "../widgets/BarDrilldownWidget";
import BarDrilldownCustomWidget from "../widgets/BarDrilldownCustomWidget";

type DashboardWidgetContentProps = {
  chartType: string;
  title: string;
  // Widget description from the dashboard detail API (kpi/sparkle include it
  // in their CSV export).
  description?: string | null;
  data: unknown;
  isLoading: boolean;
  hasError?: boolean;
  chartSpec?: Record<string, unknown>;
  isBuilderPreview?: boolean;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Special-case renderers — chart types that need props or behavior beyond the
// standard ChartWidget → ChartFactory → registry pipeline:
//
//   funnel          needs onPointClick for drilldown navigation
//   quadrant_table  needs onPointClick + renders a tier legend alongside the chart
//   data_table      not a Highcharts chart — uses DashboardTable component
//   kpi             single number display, no chart at all
//   kpi_with_detail kpi + expandable detail panel with its own sub-fetch
//   bar_drilldown   embeds its own Highcharts instance directly to keep
//                   metadata (headline/status/technical details) alongside
//                   the chart — the generic ChartWidget path drops metadata
//   single_bar      single-category 100%-stacked column with fixed,
//                   name-matched segment colors — not a registry chart type
//
// All other types (bar, pie, line, map, tiered_bar, force_bar, etc.) fall
// through to ChartWidget which delegates to ChartFactory and the registry.
// ---------------------------------------------------------------------------
type RendererFn = (p: DashboardWidgetContentProps & { displayTitle: string }) => JSX.Element;

const SPECIAL_RENDERERS: Record<string, RendererFn> = {
  funnel: (p) => (
    <FunnelWidget
      isLoading={p.isLoading}
      title={p.displayTitle}
      description={p.description}
      data={p.data}
      widgetId={p.widgetTemplateId}
    />
  ),

  data_table: (p) => (
    <DataTableWidget isLoading={p.isLoading} data={p.data} />
  ),

  kpi: (p) => (
    <KpiWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      description={p.description}
      widgetId={p.widgetTemplateId}
    />
  ),

  kpi_with_detail: (p) => (
    <KpiWithDetailWidget
      title={p.title}
      isLoading={p.isLoading}
      data={p.data}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  quadrant_table: (p) => (
    <QuadrantScatterWidget
      isLoading={p.isLoading}
      title={p.displayTitle}
      description={p.description}
      data={p.data}
      spec={p.chartSpec}
      widgetId={p.widgetTemplateId}
    />
  ),

  tiered_bar: (p) => (
    <TieredBarWidget
      title={p.displayTitle}
      data={p.data}
      widgetId={p.widgetTemplateId}
      chartSpec={p.chartSpec}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  sparkle: (p) => (
    <SparkleWidget
      isLoading={p.isLoading}
      data={p.data}
      title={p.title}
      description={p.description}
      widgetId={p.widgetTemplateId}
    />
  ),

  custom_bar: (p) => (
    <CustomBarWidget isLoading={p.isLoading} data={p.data} />
  ),

  custom_table: (p) => (
    <CustomTableWidget
      isLoading={p.isLoading}
      data={p.data}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  info_bar: (p) => (
    <InfoBarWidget isLoading={p.isLoading} data={p.data} />
  ),

  donut_pie: (p) => (
    <DonutPieWidget isLoading={p.isLoading} data={p.data} />
  ),

  single_bar: (p) => (
    <SingleBarWidget isLoading={p.isLoading} title={p.displayTitle} data={p.data} />
  ),

  custom_lines: (p) => (
    <DenialReasonsWidget
      isLoading={p.isLoading}
      data={p.data}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  deviation_bar: (p) => (
    <DeviationBarWidget
      isLoading={p.isLoading}
      data={p.data}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  average_bar: (p) => (
    <AverageBarWidget
      isLoading={p.isLoading}
      data={p.data}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  cards: (p) => (
    <CardWidget
      isLoading={p.isLoading}
      data={p.data}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  special_quadrant_chart: (p) => (
    <KpiQuadrantWidget
      title={p.displayTitle}
      data={p.data}
      widgetId={p.widgetTemplateId}
      chartSpec={p.chartSpec}
      dashboardName={p.dashboardName}
      widgetTemplateId={p.widgetTemplateId}
      appliedFilters={p.appliedFilters}
    />
  ),

  bar_drilldown: (p) => (
    <BarDrilldownWidget isLoading={p.isLoading} data={p.data} />
  ),

  bar_drilldown_custom: (p) => (
    <BarDrilldownCustomWidget isLoading={p.isLoading} data={p.data} />
  ),
};

function DashboardWidgetContent(props: DashboardWidgetContentProps): JSX.Element {
  const { chartType, title, isBuilderPreview = false, hasError = false, chartSpec = {} } = props;

  if (hasError) return <p className="text-danger mb-0">Unable to fetch data</p>;

  // In builder preview the Slot header already shows the title — pass empty string inside the chart.
  const displayTitle = isBuilderPreview ? "" : title;

  const renderSpecial = SPECIAL_RENDERERS[chartType];
  if (renderSpecial) return renderSpecial({ ...props, chartSpec, displayTitle });

  return (
    <ChartWidget
      isLoading={props.isLoading}
      title={displayTitle}
      description={props.description}
      data={props.data}
      spec={chartSpec}
      chartType={chartType}
      widgetId={props.widgetTemplateId}
    />
  );
}

export default memo(DashboardWidgetContent);
